import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Paper, IconButton,
  Chip, Divider, Alert, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, alpha, LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon, Print as PrintIcon, Add as AddIcon,
  Delete as DeleteIcon, AccessTime as AccessTimeIcon,
  Person as PersonIcon, Computer as ComputerIcon,
  Build as BuildIcon, Memory as MemoryIcon,
  Assignment as AssignmentIcon, CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon, PlayCircleFilled as PlayCircleIcon,
  Flag as FlagIcon, MedicalServices as StethoscopeIcon,
  Edit as PenIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleFilledIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SignatureCanvas from 'react-signature-canvas';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import api from '../services/api';

function OsForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewOS = !id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [computadores, setComputadores] = useState([]);
  
  // Modal de Finalização
  const [finalizarOpen, setFinalizarOpen] = useState(false);
  const [finalizando, setFinalizando] = useState(false);

  const [formData, setFormData] = useState({
    cliente_id: '',
    computador_id: '',
    data_abertura: '',
    hora_abertura: '',
    hora_inicio: '',
    hora_fim: '',
    prioridade: 'normal',
    relato_cliente: '',
    diagnosticos: [],
    servicos: [],
    pecas: [],
    upgrade_recomendado: '',
    mao_obra: 0,
    valor_pecas: 0,
    desconto: 0,
    observacoes: '',
    parcelas: 1,
    forma_pagamento: 'dinheiro',
    status: 'aberta',
    numero_os: ''
  });

  const diagnosticosComuns = [
    'Superaquecimento', 'Lento', 'Não liga', 'Tela azul',
    'Barulho estranho', 'Não carrega', 'Teclas não funcionam',
    'Wi-Fi não conecta', 'Travando', 'Desligando sozinho'
  ];

  const servicosPorCategoria = {
    'Limpeza e Manutenção': [
      'Limpeza Física', 'Troca Pasta Térmica', 'Limpeza de Fonte',
      'Limpeza de Coolers', 'Limpeza de Teclado'
    ],
    'Software e Sistema': [
      'Formatação', 'Instalação de SO', 'Backup', 'Antivírus',
      'Limpeza de Cache', 'Otimização de Sistema', 'Atualizações'
    ],
    'Diagnóstico e Reparos': [
      'Saúde dos Discos', 'Teste de Memória', 'Diagnóstico de Placa',
      'Reparo em Placa', 'Reparo em Conector'
    ],
    'Hardware': [
      'Troca de Tela', 'Troca de Bateria', 'Troca de HD/SSD',
      'Upgrade de Memória', 'Troca de Fonte', 'Troca de Teclado'
    ]
  };

  const [novaPeca, setNovaPeca] = useState({
    descricao: '',
    quantidade: 1,
    valor_unitario: 0,
    codigo: ''
  });

  // Refs para assinaturas
  const sigTecnico = useRef(null);
  const sigCliente = useRef(null);

  const clienteSelecionado = clientes.find(c => c.id === formData.cliente_id);
  const computadorSelecionado = computadores.find(c => c.id === formData.computador_id);
  const isFinalizada = formData.status === 'finalizada';

  // ===== FUNÇÕES =====
  const handleClienteChange = async (clienteId) => {
    setFormData({ ...formData, cliente_id: clienteId, computador_id: '' });
    if (clienteId) {
      try {
        const response = await api.get(`/clientes/${clienteId}/computadores`);
        setComputadores(response.data);
      } catch (error) {
        toast.error('Erro ao carregar computadores');
      }
    } else {
      setComputadores([]);
    }
  };

  const handleDiagnosticoChange = (diagnostico) => {
    if (isFinalizada) return;
    const atual = formData.diagnosticos || [];
    const novos = atual.includes(diagnostico)
      ? atual.filter(d => d !== diagnostico)
      : [...atual, diagnostico];
    setFormData({ ...formData, diagnosticos: novos });
  };

  const handleServicoChange = (servico) => {
    if (isFinalizada) return;
    const atual = formData.servicos || [];
    const novos = atual.includes(servico)
      ? atual.filter(s => s !== servico)
      : [...atual, servico];
    setFormData({ ...formData, servicos: novos });
  };

  const handleAdicionarPeca = () => {
    if (isFinalizada) return;
    if (!novaPeca.descricao || novaPeca.quantidade <= 0) {
      toast.warning('Preencha a descrição e quantidade');
      return;
    }

    const quantidade = parseFloat(novaPeca.quantidade) || 1;
    const valorUnitario = parseFloat(novaPeca.valor_unitario) || 0;
    const total = quantidade * valorUnitario;

    const nova = {
      ...novaPeca,
      quantidade,
      valor_unitario: valorUnitario,
      valor_total: total
    };

    setFormData({
      ...formData,
      pecas: [...(formData.pecas || []), nova],
      valor_pecas: (formData.valor_pecas || 0) + total
    });

    setNovaPeca({ descricao: '', quantidade: 1, valor_unitario: 0, codigo: '' });
  };

  const handleRemoverPeca = (index) => {
    if (isFinalizada) return;
    const lista = formData.pecas || [];
    const removida = lista[index];
    const totalRemovido = parseFloat(removida.valor_total) || 0;
    const novas = lista.filter((_, i) => i !== index);

    setFormData({
      ...formData,
      pecas: novas,
      valor_pecas: (formData.valor_pecas || 0) - totalRemovido
    });
  };

  const calcularTotal = () => {
    const maoObra = formData.mao_obra ? Number(formData.mao_obra) : 0;
    const totalPecas = (formData.pecas || []).reduce((sum, peca) => {
      return sum + (Number(peca.valor_total) || 0);
    }, 0);
    const desconto = formData.desconto ? Number(formData.desconto) : 0;
    const total = maoObra + totalPecas - desconto;
    return isNaN(total) ? 0 : total;
  };

  const handleSave = async () => {
    if (!formData.cliente_id) {
      toast.error('Selecione um cliente');
      return;
    }

    setSaving(true);
    try {
      const total = calcularTotal();
      const payload = {
        ...formData,
        mao_obra: parseFloat(formData.mao_obra) || 0,
        desconto: parseFloat(formData.desconto) || 0,
        valor_pecas: (formData.pecas || []).reduce((acc, p) => acc + (parseFloat(p.valor_total) || 0), 0),
        valor_total: total
      };

      if (id) {
        await api.put(`/os/${id}`, payload);
        toast.success('OS atualizada com sucesso!');
      } else {
        await api.post('/os', payload);
        toast.success('OS criada com sucesso!');
      }
      navigate('/os');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar OS');
    } finally {
      setSaving(false);
    }
  };

  // ===== ABRIR MODAL DE FINALIZAÇÃO =====
  const handleAbrirFinalizacao = () => {
    if (!formData.cliente_id) {
      toast.error('Selecione um cliente');
      return;
    }
    if ((formData.pecas || []).length === 0 && (!formData.mao_obra || formData.mao_obra <= 0)) {
      toast.warning('Adicione pelo menos uma peça ou mão de obra');
      return;
    }
    setFinalizarOpen(true);
  };

  // ===== CONFIRMAR FINALIZAÇÃO =====
  const handleConfirmarFinalizacao = async () => {
    // Verificar assinaturas
    const temAssinaturaTecnico = sigTecnico.current && !sigTecnico.current.isEmpty();
    const temAssinaturaCliente = sigCliente.current && !sigCliente.current.isEmpty();

    if (!temAssinaturaTecnico) {
      toast.error('Assinatura do técnico é obrigatória');
      return;
    }
    if (!temAssinaturaCliente) {
      toast.error('Assinatura do cliente é obrigatória');
      return;
    }

    setFinalizando(true);
    try {
      const agora = new Date();
      const dataFim = agora.toISOString().split('T')[0];
      const horaFim = agora.toTimeString().slice(0, 5);

      const payload = {
        data_fechamento: dataFim,
        hora_fechamento: horaFim,
        assinatura_tecnico: sigTecnico.current.toDataURL(),
        assinatura_cliente: sigCliente.current.toDataURL(),
        valor_total: calcularTotal()
      };

      await api.post(`/os/${id}/finalizar`, payload);
      
      toast.success('OS finalizada com sucesso!');
      setFinalizarOpen(false);
      navigate('/os');
    } catch (error) {
      console.error('Erro ao finalizar:', error);
      toast.error(error.response?.data?.error || 'Erro ao finalizar OS');
    } finally {
      setFinalizando(false);
    }
  };

  const handlePrint = () => {
    const token = localStorage.getItem('@Jobson:token');
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }
    window.open(`/api/os/${id}/print?token=${token}`, '_blank');
  };

  // ===== EFFECT PRINCIPAL =====
  useEffect(() => {
    const carregarClientes = async () => {
      try {
        const response = await api.get('/clientes');
        setClientes(response.data || []);
      } catch (error) {
        toast.error('Erro ao carregar clientes');
      }
    };
    carregarClientes();

    if (id) {
      const carregarOS = async () => {
        try {
          const response = await api.get(`/os/${id}`);
          const osData = response.data;

          // Processar dados
          if (osData.data_abertura && typeof osData.data_abertura === 'string') {
            osData.data_abertura = osData.data_abertura.split('T')[0];
          }
          osData.diagnosticos = Array.isArray(osData.diagnosticos) ? osData.diagnosticos : [];
          osData.servicos = Array.isArray(osData.servicos) ? osData.servicos : [];
          osData.pecas = Array.isArray(osData.pecas) ? osData.pecas : [];
          osData.mao_obra = parseFloat(osData.mao_obra) || 0;
          osData.valor_pecas = parseFloat(osData.valor_pecas) || 0;
          osData.desconto = parseFloat(osData.desconto) || 0;

          setFormData(osData);

          if (osData.cliente_id) {
            const compRes = await api.get(`/clientes/${osData.cliente_id}/computadores`);
            setComputadores(compRes.data || []);
          }
        } catch (error) {
          toast.error('Erro ao carregar OS');
        }
      };
      carregarOS();
    } else {
      const agora = new Date();
      setFormData(prev => ({
        ...prev,
        data_abertura: agora.toISOString().split('T')[0],
        hora_abertura: agora.toTimeString().slice(0, 5),
        hora_inicio: agora.toTimeString().slice(0, 5)
      }));
    }
  }, [id]);

  if (loading) {
    return (
      <Layout title={id ? 'Carregando...' : 'Nova OS'}>
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha('#0A2647', 0.1),
            '& .MuiLinearProgress-bar': { 
              backgroundColor: '#0A2647',
              borderRadius: 4
            }
          }} />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title={id ? `OS #${formData.numero_os || id}` : 'Nova OS'}>
      <Box sx={{
        maxWidth: { xs: '100%', sm: 600, md: 800, lg: 1000 },
        width: '100%',
        mx: 'auto',
        bgcolor: '#ffffff',
        borderRadius: { xs: 0, sm: 2 },
        minHeight: { xs: 'auto', sm: '100vh' },
        pb: 4,
        px: { xs: 2, sm: 2, md: 3 },
        overflowX: 'hidden'
      }}>
        {/* Cabeçalho */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 1.5, sm: 2 },
          bgcolor: 'white',
          borderBottom: '1px solid #eef2f6',
          mb: { xs: 0, sm: 2 }
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#0b2b4f', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            {id ? `OS nº ${formData.numero_os || id}` : 'Nova OS'}
          </Typography>
          <Box sx={{
            bgcolor: isFinalizada ? '#e8f5e9' : '#e6f2ff',
            color: isFinalizada ? '#2e7d32' : '#0066cc',
            borderRadius: 30,
            px: 2,
            py: 0.8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: { xs: 11, sm: 12 }
          }}>
            <AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
            <span>{isFinalizada ? 'Finalizada' : 'Em andamento'}</span>
          </Box>
        </Box>

        {/* Horários */}
        <Grid container spacing={2} sx={{ px: { xs: 1, sm: 2 }, mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ bgcolor: '#eaf6e7', color: '#1e7e34', p: 1.5, borderRadius: 30, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: 13, sm: 14 } }}>
              <PlayCircleIcon fontSize="small" />
              <span>
                <strong>Abertura:</strong> {
                  formData.data_abertura
                    ? new Date(formData.data_abertura + 'T12:00:00').toLocaleDateString('pt-BR')
                    : 'Data não informada'
                } às {formData.hora_abertura || formData.hora_inicio || '--:--'}
              </span>
            </Box>
          </Grid>
          {isFinalizada && formData.hora_fim && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', p: 1.5, borderRadius: 30, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: 13, sm: 14 } }}>
                <FlagIcon fontSize="small" />
                <span><strong>Finalizado:</strong> {new Date().toLocaleDateString('pt-BR')} às {formData.hora_fim}</span>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Cliente / Computador */}
        <Paper sx={{ mx: { xs: 1, sm: 2 }, p: 2, borderRadius: 4, mb: 2, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ bgcolor: '#f0f4fa', borderRadius: 3, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PersonIcon sx={{ color: '#2a5c9a' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7a8f', textTransform: 'uppercase' }}>CLIENTE</Typography>
                  {clienteSelecionado ? (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{clienteSelecionado.nome_razao}</Typography>
                      <Typography variant="caption" sx={{ color: '#4f6f8f' }}>{clienteSelecionado.telefone || ''}</Typography>
                    </>
                  ) : (
                    <FormControl fullWidth size="small" sx={{ mt: 1 }} disabled={isFinalizada}>
                      <InputLabel>Selecione o cliente</InputLabel>
                      <Select
                        value={formData.cliente_id}
                        label="Selecione o cliente"
                        onChange={(e) => handleClienteChange(e.target.value)}
                      >
                        {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.nome_razao}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ bgcolor: '#f0f4fa', borderRadius: 3, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ComputerIcon sx={{ color: '#2a5c9a' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6b7a8f', textTransform: 'uppercase' }}>COMPUTADOR</Typography>
                  {computadorSelecionado ? (
                    <>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{computadorSelecionado.nome_identificacao}</Typography>
                      <Typography variant="caption" sx={{ color: '#4f6f8f' }}>{computadorSelecionado.modelo}</Typography>
                    </>
                  ) : (
                    <FormControl fullWidth size="small" sx={{ mt: 1 }} disabled={!formData.cliente_id || isFinalizada}>
                      <InputLabel>Selecione o computador</InputLabel>
                      <Select
                        value={formData.computador_id}
                        label="Selecione o computador"
                        onChange={(e) => setFormData({ ...formData, computador_id: e.target.value })}
                      >
                        {computadores.map(c => <MenuItem key={c.id} value={c.id}>{c.nome_identificacao} - {c.modelo}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Data e Prioridade */}
        <Grid container spacing={2} sx={{ px: { xs: 1, sm: 2 }, mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Data de Abertura"
              type="date"
              size="small"
              value={formData.data_abertura}
              onChange={(e) => !isFinalizada && isNewOS && setFormData({ ...formData, data_abertura: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={isFinalizada || !isNewOS}
              sx={{ bgcolor: 'white', borderRadius: 3 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" sx={{ bgcolor: 'white', borderRadius: 3 }} disabled={isFinalizada}>
              <InputLabel>Prioridade</InputLabel>
              <Select
                value={formData.prioridade}
                label="Prioridade"
                onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}
              >
                <MenuItem value="baixa">Baixa</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="urgente">Urgente</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Relato do Cliente */}
        <Box sx={{ px: { xs: 1, sm: 2 }, mb: 2 }}>
          <TextField
            fullWidth
            label="Relato do cliente"
            multiline
            rows={3}
            value={formData.relato_cliente}
            onChange={(e) => !isFinalizada && setFormData({ ...formData, relato_cliente: e.target.value })}
            disabled={isFinalizada}
            sx={{ bgcolor: 'white', borderRadius: 3 }}
          />
        </Box>

        {/* Diagnósticos */}
        <Box sx={{ px: { xs: 1, sm: 2 }, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <StethoscopeIcon sx={{ color: '#2a5c9a' }} />
            <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>Diagnósticos</Typography>
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 1
          }}>
            {diagnosticosComuns.map(d => (
              <Chip
                key={d}
                label={d}
                onClick={() => !isFinalizada && handleDiagnosticoChange(d)}
                icon={formData.diagnosticos?.includes(d) ? <CheckCircleIcon /> : undefined}
                color={formData.diagnosticos?.includes(d) ? 'primary' : 'default'}
                disabled={isFinalizada}
                sx={{
                  borderRadius: 30,
                  fontWeight: 500,
                  bgcolor: formData.diagnosticos?.includes(d) ? '#2a5c9a' : '#f0f4fa',
                  color: formData.diagnosticos?.includes(d) ? 'white' : '#1e2f44',
                  '& .MuiChip-icon': { color: 'white' },
                  opacity: isFinalizada ? 0.7 : 1,
                  pointerEvents: isFinalizada ? 'none' : 'auto'
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Serviços */}
        <Box sx={{ px: { xs: 1, sm: 2 }, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BuildIcon sx={{ color: '#2a5c9a' }} />
            <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>Serviços</Typography>
          </Box>
          {Object.entries(servicosPorCategoria).map(([categoria, servicos]) => (
            <Box key={categoria} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#2a5c9a', fontWeight: 600, mb: 1 }}>{categoria}</Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 1
              }}>
                {servicos.map(s => (
                  <Chip
                    key={s}
                    label={s}
                    onClick={() => !isFinalizada && handleServicoChange(s)}
                    icon={formData.servicos?.includes(s) ? <CheckCircleIcon /> : undefined}
                    color={formData.servicos?.includes(s) ? 'primary' : 'default'}
                    disabled={isFinalizada}
                    sx={{
                      borderRadius: 30,
                      fontWeight: 500,
                      bgcolor: formData.servicos?.includes(s) ? '#2a5c9a' : '#f0f4fa',
                      color: formData.servicos?.includes(s) ? 'white' : '#1e2f44',
                      '& .MuiChip-icon': { color: 'white' },
                      opacity: isFinalizada ? 0.7 : 1,
                      pointerEvents: isFinalizada ? 'none' : 'auto'
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Peças */}
        <Box sx={{ px: { xs: 1, sm: 2 }, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MemoryIcon sx={{ color: '#2a5c9a' }} />
            <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>Peças</Typography>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 1,
            mb: 2
          }}>
            {(formData.pecas || []).map((peca, index) => (
              <Paper key={index} sx={{ p: 1.5, borderRadius: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: isFinalizada ? 0.7 : 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{peca.descricao}</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7a8f' }}>
                    {peca.quantidade}x R$ {Number(peca.valor_unitario).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    R$ {Number(peca.valor_total).toFixed(2)}
                  </Typography>
                  {!isFinalizada && (
                    <IconButton size="small" onClick={() => handleRemoverPeca(index)}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>

          {!isFinalizada && (
            <Paper sx={{ p: 2, borderRadius: 3, bgcolor: '#f8fafd', border: '1px dashed #9bb6d0' }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Descrição"
                    value={novaPeca.descricao}
                    onChange={(e) => setNovaPeca({ ...novaPeca, descricao: e.target.value })}
                  />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Qtd"
                    value={novaPeca.quantidade}
                    onChange={(e) => setNovaPeca({ ...novaPeca, quantidade: parseFloat(e.target.value) || 1 })}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Grid>
                <Grid item xs={4} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Valor unit."
                    value={novaPeca.valor_unitario}
                    onChange={(e) => setNovaPeca({ ...novaPeca, valor_unitario: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={4} sm={3}>
                  <Button fullWidth variant="contained" onClick={handleAdicionarPeca} sx={{ height: '100%' }}>
                    <AddIcon /> Adicionar
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>

        {/* Valores */}
        <Box sx={{ px: { xs: 1, sm: 2 }, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MoneyIcon sx={{ color: '#2a5c9a' }} />
            <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>Valores</Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Mão de obra (R$)"
                type="number"
                value={formData.mao_obra}
                onChange={(e) => !isFinalizada && setFormData({ ...formData, mao_obra: parseFloat(e.target.value) || 0 })}
                disabled={isFinalizada}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Desconto (R$)"
                type="number"
                value={formData.desconto}
                onChange={(e) => !isFinalizada && setFormData({ ...formData, desconto: parseFloat(e.target.value) || 0 })}
                disabled={isFinalizada}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={isFinalizada}>
                <InputLabel>Forma de pagamento</InputLabel>
                <Select
                  value={formData.forma_pagamento}
                  label="Forma de pagamento"
                  onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                >
                  <MenuItem value="dinheiro">Dinheiro</MenuItem>
                  <MenuItem value="credito">Cartão de Crédito</MenuItem>
                  <MenuItem value="debito">Cartão de Débito</MenuItem>
                  <MenuItem value="pix">PIX</MenuItem>
                  <MenuItem value="transferencia">Transferência</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Paper sx={{
            background: 'linear-gradient(135deg, #0b2b4f 0%, #1b4a7a 100%)',
            color: 'white',
            p: 2,
            borderRadius: 30,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography>Total</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              R$ {calcularTotal().toFixed(2)}
            </Typography>
          </Paper>
        </Box>

        {/* Botões de ação */}
        <Box sx={{
          px: { xs: 2, sm: 2 },
          display: 'flex',
          gap: 1,
          mb: 4,
          flexWrap: 'wrap',
          justifyContent: 'center',
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button
            sx={{ minWidth: { xs: '100%', sm: 120 } }}
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/os')}
            disabled={saving || finalizando}
          >
            Cancelar
          </Button>

          {!isFinalizada && (
            <>
              <Button
                sx={{ minWidth: { xs: '100%', sm: 120 }, bgcolor: '#2a5c9a' }}
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || finalizando}
              >
                Salvar
              </Button>

              {id && (
                <Button
                  sx={{ 
                    minWidth: { xs: '100%', sm: 200 },
                    background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(46,204,113,0.3)'
                    }
                  }}
                  variant="contained"
                  startIcon={<CheckCircleFilledIcon />}
                  onClick={handleAbrirFinalizacao}
                  disabled={saving || finalizando}
                >
                  Finalizar OS
                </Button>
              )}
            </>
          )}

          {id && (
            <Button
              sx={{ minWidth: { xs: '100%', sm: 120 } }}
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={saving || finalizando}
            >
              Imprimir
            </Button>
          )}
        </Box>
      </Box>

      {/* ===== MODAL DE FINALIZAÇÃO ===== */}
      <Dialog
        open={finalizarOpen}
        onClose={() => !finalizando && setFinalizarOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 40px 80px rgba(0,0,0,0.2)',
            maxHeight: '90vh'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%'
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 250,
            height: 250,
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '50%'
          }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
            <Avatar sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              width: 56,
              height: 56
            }}>
              <CheckCircleFilledIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                Finalizar OS #{formData.numero_os || id}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Confirme os dados e assine para finalizar o atendimento
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Resumo da OS */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0A2647', mb: 2 }}>
              📋 Resumo da OS
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Cliente</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {clienteSelecionado?.nome_razao || 'Não informado'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Computador</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {computadorSelecionado?.nome_identificacao || 'Não informado'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Mão de Obra</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                    R$ {Number(formData.mao_obra || 0).toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, bgcolor: alpha('#0A2647', 0.02), borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Peças</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                    {formData.pecas?.length || 0} peça(s) - R$ {Number(formData.valor_pecas || 0).toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2, 
                  background: 'linear-gradient(135deg, #0A2647 0%, #1a3b5c 100%)',
                  color: 'white',
                  borderRadius: 2
                }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      TOTAL
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      R$ {calcularTotal().toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Assinaturas */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0A2647', mb: 2 }}>
            ✍️ Assinaturas
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '2px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647', mb: 1 }}>
                  Técnico Responsável
                </Typography>
                <Box sx={{ 
                  border: '2px dashed #ccd9e9', 
                  borderRadius: 2, 
                  height: 120, 
                  bgcolor: '#f8fafd',
                  overflow: 'hidden'
                }}>
                  <SignatureCanvas
                    ref={sigTecnico}
                    canvasProps={{ 
                      width: 400, 
                      height: 110, 
                      style: { width: '100%', height: '100%' } 
                    }}
                  />
                </Box>
                <Button 
                  size="small" 
                  onClick={() => sigTecnico.current?.clear()}
                  sx={{ mt: 1 }}
                >
                  Limpar
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '2px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647', mb: 1 }}>
                  Cliente
                </Typography>
                <Box sx={{ 
                  border: '2px dashed #ccd9e9', 
                  borderRadius: 2, 
                  height: 120, 
                  bgcolor: '#f8fafd',
                  overflow: 'hidden'
                }}>
                  <SignatureCanvas
                    ref={sigCliente}
                    canvasProps={{ 
                      width: 400, 
                      height: 110, 
                      style: { width: '100%', height: '100%' } 
                    }}
                  />
                </Box>
                <Button 
                  size="small" 
                  onClick={() => sigCliente.current?.clear()}
                  sx={{ mt: 1 }}
                >
                  Limpar
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button 
            onClick={() => setFinalizarOpen(false)} 
            variant="outlined"
            fullWidth={false}
            sx={{ 
              borderRadius: 2,
              color: '#0A2647',
              borderColor: '#0A2647',
              '&:hover': {
                borderColor: '#0A2647',
                bgcolor: alpha('#0A2647', 0.04)
              }
            }}
            disabled={finalizando}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarFinalizacao}
            variant="contained"
            disabled={finalizando}
            startIcon={finalizando ? <CircularProgress size={20} color="inherit" /> : <CheckCircleFilledIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #27ae60 0%, #1e8449 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(46,204,113,0.3)'
              },
              '&:disabled': {
                bgcolor: alpha('#2ecc71', 0.5),
                color: 'white'
              }
            }}
          >
            {finalizando ? 'Finalizando...' : 'Confirmar Finalização'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default OsForm;
